/**
 * Construction IDF Synchronization
 *
 * Creates and updates Construction and Material IDF objects
 * from geometry editing operations.
 */

import type { IDFObject, IDFField } from '../../parser/types';
import type { ConstructionAssembly, MaterialLayer, MaterialProperties, AirGapProperties } from '../editingTypes';
import { CONSTRUCTION_FIELD_MAPPINGS, MATERIAL_FIELD_MAPPINGS, AIR_GAP_FIELD_MAPPINGS } from './fieldMappings';

/**
 * Result of creating a construction IDF object.
 */
export interface ConstructionIDFResult {
  /** The created Construction IDF object */
  construction: Omit<IDFObject, 'id'>;
}

/**
 * Create a field object for IDF.
 */
function createField(value: string, comment?: string): IDFField {
  return {
    value,
    ...(comment && { inlineComment: `!- ${comment}` }),
  };
}

/**
 * Create a Construction IDF object.
 */
export function createConstructionIDFObject(
  name: string,
  layerNames: string[]
): ConstructionIDFResult {
  const fields: IDFField[] = [createField(name, 'Name')];

  // Add layers (outside to inside)
  layerNames.forEach((layerName, i) => {
    const comment = i === 0 ? 'Outside Layer' : `Layer ${i + 1}`;
    fields.push(createField(layerName, comment));
  });

  return {
    construction: {
      className: 'Construction',
      fields,
      leadingComments: [],
    },
  };
}

/**
 * Update a Construction IDF object.
 */
export function updateConstructionIDFObject(
  constructionObject: IDFObject,
  updates: {
    name?: string;
    layerNames?: string[];
  }
): IDFObject {
  const fields = [...constructionObject.fields];

  if (updates.name !== undefined) {
    if (fields[CONSTRUCTION_FIELD_MAPPINGS.NAME.index]) {
      fields[CONSTRUCTION_FIELD_MAPPINGS.NAME.index] = {
        ...fields[CONSTRUCTION_FIELD_MAPPINGS.NAME.index],
        value: updates.name,
      };
    }
  }

  if (updates.layerNames !== undefined) {
    // Remove old layers
    fields.splice(CONSTRUCTION_FIELD_MAPPINGS.LAYERS_START.index);

    // Add new layers
    updates.layerNames.forEach((layerName, i) => {
      const comment = i === 0 ? 'Outside Layer' : `Layer ${i + 1}`;
      fields.push(createField(layerName, comment));
    });
  }

  return { ...constructionObject, fields };
}

/**
 * Create a Material IDF object.
 */
export function createMaterialIDFObject(props: MaterialProperties): Omit<IDFObject, 'id'> {
  const fields: IDFField[] = [
    createField(props.name, 'Name'),
    createField(props.roughness, 'Roughness'),
    createField(String(props.thickness), 'Thickness'),
    createField(String(props.conductivity), 'Conductivity'),
    createField(String(props.density), 'Density'),
    createField(String(props.specificHeat), 'Specific Heat'),
  ];

  if (props.thermalAbsorptance !== undefined) {
    fields.push(createField(String(props.thermalAbsorptance), 'Thermal Absorptance'));
  }
  if (props.solarAbsorptance !== undefined) {
    fields.push(createField(String(props.solarAbsorptance), 'Solar Absorptance'));
  }
  if (props.visibleAbsorptance !== undefined) {
    fields.push(createField(String(props.visibleAbsorptance), 'Visible Absorptance'));
  }

  return {
    className: 'Material',
    fields,
    leadingComments: [],
  };
}

/**
 * Create a Material:AirGap IDF object.
 */
export function createAirGapIDFObject(props: AirGapProperties): Omit<IDFObject, 'id'> {
  return {
    className: 'Material:AirGap',
    fields: [
      createField(props.name, 'Name'),
      createField(String(props.thermalResistance), 'Thermal Resistance'),
    ],
    leadingComments: [],
  };
}

/**
 * Get construction name from IDF object.
 */
export function getConstructionName(constructionObject: IDFObject): string {
  return constructionObject.fields[CONSTRUCTION_FIELD_MAPPINGS.NAME.index]?.value ?? '';
}

/**
 * Get construction layer names from IDF object.
 */
export function getConstructionLayers(constructionObject: IDFObject): string[] {
  const layers: string[] = [];

  for (let i = CONSTRUCTION_FIELD_MAPPINGS.LAYERS_START.index; i < constructionObject.fields.length; i++) {
    const field = constructionObject.fields[i];
    if (field && field.value.trim()) {
      layers.push(field.value);
    }
  }

  return layers;
}

/**
 * Parse material properties from IDF object.
 */
export function parseMaterialProperties(materialObject: IDFObject): MaterialProperties | null {
  const fields = materialObject.fields;

  const name = fields[MATERIAL_FIELD_MAPPINGS.NAME.index]?.value;
  const roughness = fields[MATERIAL_FIELD_MAPPINGS.ROUGHNESS.index]?.value as MaterialProperties['roughness'];
  const thickness = parseFloat(fields[MATERIAL_FIELD_MAPPINGS.THICKNESS.index]?.value ?? '0');
  const conductivity = parseFloat(fields[MATERIAL_FIELD_MAPPINGS.CONDUCTIVITY.index]?.value ?? '0');
  const density = parseFloat(fields[MATERIAL_FIELD_MAPPINGS.DENSITY.index]?.value ?? '0');
  const specificHeat = parseFloat(fields[MATERIAL_FIELD_MAPPINGS.SPECIFIC_HEAT.index]?.value ?? '0');

  if (!name) return null;

  const result: MaterialProperties = {
    name,
    roughness: roughness || 'MediumSmooth',
    thickness,
    conductivity,
    density,
    specificHeat,
  };

  const thermalAbsorptance = fields[MATERIAL_FIELD_MAPPINGS.THERMAL_ABSORPTANCE.index]?.value;
  if (thermalAbsorptance) {
    result.thermalAbsorptance = parseFloat(thermalAbsorptance);
  }

  const solarAbsorptance = fields[MATERIAL_FIELD_MAPPINGS.SOLAR_ABSORPTANCE.index]?.value;
  if (solarAbsorptance) {
    result.solarAbsorptance = parseFloat(solarAbsorptance);
  }

  const visibleAbsorptance = fields[MATERIAL_FIELD_MAPPINGS.VISIBLE_ABSORPTANCE.index]?.value;
  if (visibleAbsorptance) {
    result.visibleAbsorptance = parseFloat(visibleAbsorptance);
  }

  return result;
}

/**
 * Parse air gap properties from IDF object.
 */
export function parseAirGapProperties(airGapObject: IDFObject): AirGapProperties | null {
  const fields = airGapObject.fields;

  const name = fields[AIR_GAP_FIELD_MAPPINGS.NAME.index]?.value;
  const thermalResistance = parseFloat(fields[AIR_GAP_FIELD_MAPPINGS.THERMAL_RESISTANCE.index]?.value ?? '0');

  if (!name) return null;

  return { name, thermalResistance };
}

/**
 * Calculate U-value for a construction (simplified calculation).
 * U = 1 / (Rsi + sum(thickness/conductivity) + Rso)
 * Where Rsi and Rso are inside/outside surface resistances.
 */
export function calculateUValue(layers: Array<{ thickness: number; conductivity: number } | { thermalResistance: number }>): number {
  const Rsi = 0.13; // Inside surface resistance (m²K/W)
  const Rso = 0.04; // Outside surface resistance (m²K/W)

  let totalR = Rsi + Rso;

  for (const layer of layers) {
    if ('thermalResistance' in layer) {
      // Air gap or no-mass material
      totalR += layer.thermalResistance;
    } else {
      // Regular material
      if (layer.conductivity > 0) {
        totalR += layer.thickness / layer.conductivity;
      }
    }
  }

  return totalR > 0 ? 1 / totalR : 0;
}

/**
 * Calculate R-value for a construction.
 */
export function calculateRValue(layers: Array<{ thickness: number; conductivity: number } | { thermalResistance: number }>): number {
  const uValue = calculateUValue(layers);
  return uValue > 0 ? 1 / uValue : 0;
}

/**
 * Extract construction assembly from IDF document.
 */
export function extractConstructionAssembly(
  constructionObject: IDFObject,
  materialObjects: Map<string, IDFObject>
): ConstructionAssembly {
  const name = getConstructionName(constructionObject);
  const layerNames = getConstructionLayers(constructionObject);

  const layers: MaterialLayer[] = [];
  const layerData: Array<{ thickness: number; conductivity: number } | { thermalResistance: number }> = [];

  let totalThickness = 0;

  layerNames.forEach((layerName, index) => {
    const materialObj = materialObjects.get(layerName.toLowerCase());
    if (!materialObj) {
      // Material not found - create placeholder
      layers.push({
        id: `layer_${index}`,
        materialName: layerName,
        thickness: 0,
        category: 'other',
      });
      return;
    }

    const className = materialObj.className.toLowerCase();

    if (className === 'material:airgap') {
      const props = parseAirGapProperties(materialObj);
      if (props) {
        layers.push({
          id: `layer_${index}`,
          materialName: layerName,
          thickness: 0.025, // Default air gap visual thickness
          category: 'air-gap',
        });
        layerData.push({ thermalResistance: props.thermalResistance });
      }
    } else if (className === 'material') {
      const props = parseMaterialProperties(materialObj);
      if (props) {
        layers.push({
          id: `layer_${index}`,
          materialName: layerName,
          thickness: props.thickness,
          category: determineMaterialCategory(props),
        });
        layerData.push({ thickness: props.thickness, conductivity: props.conductivity });
        totalThickness += props.thickness;
      }
    }
  });

  const uValue = calculateUValue(layerData);
  const rValue = uValue > 0 ? 1 / uValue : 0;

  return {
    name,
    type: 'opaque',
    layers,
    totalThickness,
    uValue,
    rValue,
    sourceId: constructionObject.id,
  };
}

/**
 * Determine material category based on properties.
 */
function determineMaterialCategory(props: MaterialProperties): 'masonry' | 'insulation' | 'air-gap' | 'finish' | 'other' {
  // Heuristics based on material properties

  // Low conductivity = insulation
  if (props.conductivity < 0.1) {
    return 'insulation';
  }

  // High density = masonry
  if (props.density > 1500) {
    return 'masonry';
  }

  // Thin and smooth = finish
  if (props.thickness < 0.03 && ['Smooth', 'VerySmooth'].includes(props.roughness)) {
    return 'finish';
  }

  return 'other';
}

/**
 * Extract all constructions from IDF document.
 */
export function extractAllConstructions(
  constructionObjects: IDFObject[],
  materialObjects: IDFObject[]
): ConstructionAssembly[] {
  // Create material lookup map
  const materialMap = new Map<string, IDFObject>();
  for (const obj of materialObjects) {
    const name = obj.fields[0]?.value;
    if (name) {
      materialMap.set(name.toLowerCase(), obj);
    }
  }

  // Extract constructions
  return constructionObjects.map((obj) => extractConstructionAssembly(obj, materialMap));
}
